import { computed, DestroyRef, effect, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BaseViewModel } from '../base-view-model';
import { RoutePaths } from '@helper/route-paths';
import { MetaData } from '@helper/meta-data';
import { ListRatingsRequest, ListRatingsResponse } from '@proto/store/api/store/orders/ratings';
import { ProductMeta, Rating, Rating_SkuInfo } from '@proto/store/api/common/common';
import { RatingRepositoryService } from '@service/rating-repository.service';
import { Constants } from '@helper/constants';

import { isEqual } from 'lodash-es';

@Injectable()
export class RatingModel extends BaseViewModel<ListRatingsResponse> {
  private static pageSize = 200;

  readonly nextPageToken = signal('');
  readonly ratings = computed(() => this.allRatings().slice(0, this.itemCount()), {
    equal: (a, b) => isEqual(a, b),
  });
  readonly itemCount = signal(Constants.displayCount(this.detectService.isMobileDevice()));
  readonly allRatings = signal<Rating[]>([]);
  readonly skuInfos = signal<{ [key: string]: Rating_SkuInfo }>({});
  readonly productMetas = signal<{ [key: string]: ProductMeta }>({});

  constructor(private lativRepository: RatingRepositoryService) {
    super(ListRatingsResponse.fromPartial({}));
    effect(() => this.allRatings.update(items => items.concat(this.apiResult().ratings)));
    effect(() => this.skuInfos.update(map => Object.assign(map, this.apiResult().skuInfos)));
    effect(() => this.productMetas.update(map => Object.assign(map, this.apiResult().productMetas)));
    effect(() => this.nextPageToken.set(this.apiResult().nextPageToken));
  }

  listRatings(ref: DestroyRef, productId: string = ''): void {
    const pageToken = this.nextPageToken();
    this.nextPageToken.set('');
    this.lativRepository
      .listRatings(
        ListRatingsRequest.fromPartial({
          productId,
          pageSize: RatingModel.pageSize,
          pageToken,
        })
      )
      .pipe(takeUntilDestroyed(ref))
      .subscribe({
        next: res => this.setApiResult(res),
        error: err => {
          this.nextPageToken.set(pageToken);
          this.setApiError(err);
        },
      });
  }

  override getMetaByUrl(segments: string[]): MetaData | undefined {
    if (segments[0] !== RoutePaths.COMMENTS) {
      return undefined;
    }
    return new MetaData(segments[1] ? $localize`商品評價` : $localize`最新評價`);
  }
}
